import {
  LineupGenerationRequest,
  LineupGenerationResponse,
  BatchLineupGenerationRequest,
  BatchLineupGenerationResponse,
  LeagueRankingsRequest,
  LeagueRankingsResponse,
  TradeEvaluationRequest,
  TradeEvaluationResponse,
  TeamInfo,
  TeamInfoRequest,
  TeamInfoResponse,
  CustomLineupEvaluationRequest,
  MatchLineupEvaluationResponse,
  MatchLineupEvaluationRequest,
  MatchLineupDataResponse,
  PlayerPoolResponse,
  TeamRanking,
  SaveLeagueRankingsResponse,
  CheckSavedRankingsResponse,
  BattingOrderOptimizationRequest,
  BattingOrderOptimizationResponse,
  NextBatterRecommendationRequest,
  NextBatterRecommendationResponse,
  SalaryPlaygroundResponse,
  RankingsHistoryResponse,
} from '../types/lineup';
import {
  AssistantChatRequest,
  AssistantChatResponse,
  AutopilotChatRequest,
  AutopilotChatResponse,
} from '../types/assistant';

// API Configuration
// Use environment variable or default to localhost for development
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

// Auth Response Types
export interface LoginResponse {
  success: boolean;
  token?: string;
  username?: string;
  message: string;
  expires_in?: number;
}

export interface VerifyResponse {
  valid: boolean;
  username?: string;
  message: string;
}

// Projections Response Types
export interface ProjectionRow {
  [key: string]: string | number | null;
}

export interface ProjectionsResponse {
  target_season: number | null;
  available_seasons: number[];
  rows: ProjectionRow[];
}

// API Service Class
class ApiService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = API_BASE_URL;
    console.log('API Base URL:', this.baseUrl);
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    
    // Get auth token from localStorage
    const token = localStorage.getItem('authToken');
    
    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        let errorMessage = `API Error: ${response.status} ${response.statusText}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorData.message || errorMessage;
        } catch (e) {
          // If response is not JSON, use status text
        }
        const error = new Error(errorMessage);
        (error as any).status = response.status;
        (error as any).detail = errorMessage;
        throw error;
      }

      return await response.json();
    } catch (error) {
      console.error('API Request failed:', error);
      throw error;
    }
  }

  // ==================== Authentication APIs ====================

  /**
   * Login with username and password
   * POST /api/v2/auth/login
   */
  async login(username: string, password: string): Promise<LoginResponse> {
    return this.request('/api/v2/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
  }

  /**
   * Verify if the current token is valid
   * POST /api/v2/auth/verify
   */
  async verifyToken(): Promise<VerifyResponse> {
    const token = localStorage.getItem('authToken');
    if (!token) {
      return { valid: false, message: 'No token found' };
    }
    
    try {
      return await this.request('/api/v2/auth/verify', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
    } catch (error) {
      return { valid: false, message: 'Token verification failed' };
    }
  }

  /**
   * Get current authenticated user info
   * GET /api/v2/auth/me
   */
  async getCurrentUser(): Promise<{ username: string; authenticated: boolean }> {
    return this.request('/api/v2/auth/me');
  }

  /**
   * Verify a magic link token and get an access token
   * POST /api/v2/auth/verify-magic-link
   */
  async verifyMagicLink(token: string): Promise<LoginResponse> {
    return this.request(`/api/v2/auth/verify-magic-link?token=${encodeURIComponent(token)}`, {
      method: 'POST',
    });
  }

  // Health Check
  async healthCheck(): Promise<any> {
    return this.request('/api/health');
  }

  // Team Statistics
  async getTeamStatistics(teamId: number = 3): Promise<any> {
    return this.request(`/api/teams/${teamId}/statistics`);
  }

  // Team Players
  async getTeamPlayers(teamId: number = 3): Promise<any> {
    return this.request(`/api/teams/${teamId}/players`);
  }

  // Generate Lineups
  async generateLineups(teamId: number = 3, nLineups: number = 3): Promise<any> {
    return this.request(`/api/teams/${teamId}/lineups?n_lineups=${nLineups}`, {
      method: 'POST',
    });
  }

  // Get Remaining Players
  async getRemainingPlayers(teamId: number, currentSquad: any[]): Promise<any> {
    return this.request(`/api/teams/${teamId}/remaining-players`, {
      method: 'POST',
      body: JSON.stringify({
        current_squad: currentSquad,
      }),
    });
  }

  // Substitute Player
  async substitutePlayer(
    teamId: number, 
    lineupId: number, 
    playerToRemove: any, 
    playerToAdd: any, 
    position: number
  ): Promise<any> {
    return this.request(`/api/teams/${teamId}/substitute-player`, {
      method: 'POST',
      body: JSON.stringify({
        lineup_id: lineupId,
        player_to_remove: playerToRemove,
        player_to_add: playerToAdd,
        position: position,
      }),
    });
  }

  // Run Simulation
  async runSimulation(teamId: number = 3, lineupId: number = 1, nSimulations: number = 1000): Promise<any> {
    return this.request(`/api/teams/${teamId}/simulate?lineup_id=${lineupId}&n_simulations=${nSimulations}`, {
      method: 'POST',
    });
  }

  // Trade Analysis
  async analyzeTradeScenario(
    teamId: number,
    playersOut: number[],
    playersIn: number[],
    scenarioName: string = 'Trade Analysis'
  ): Promise<any> {
    return this.request(`/api/teams/${teamId}/trade-analysis`, {
      method: 'POST',
      body: JSON.stringify({
        players_out: playersOut,
        players_in: playersIn,
        scenario_name: scenarioName,
      }),
    });
  }

  // ==================== V2 Lineup APIs (Production) ====================

  /**
   * Get all available teams (V2)
   * GET /api/v2/lineups/teams
   */
  async getTeamsV2(): Promise<{ teams: TeamInfo[] }> {
    return this.request('/api/v2/lineups/teams');
  }

  /**
   * Generate lineups for a single team (V2)
   * POST /api/v2/lineups/generate
   * @param useOptimized - Whether to use the optimized vectorized approach
   */
  async generateLineupsV2(
    request: LineupGenerationRequest, 
    useOptimized: boolean = true
  ): Promise<LineupGenerationResponse> {
    return this.request(`/api/v2/lineups/generate?use_optimized=${useOptimized}`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  /**
   * Generate lineups for multiple teams in batch (V2)
   * POST /api/v2/lineups/generate-batch
   * @param useOptimized - Whether to use the optimized vectorized approach
   */
  async batchGenerateLineupsV2(
    request: BatchLineupGenerationRequest,
    useOptimized: boolean = true
  ): Promise<BatchLineupGenerationResponse> {
    return this.request(`/api/v2/lineups/generate-batch?use_optimized=${useOptimized}`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  /**
   * Get league-wide team rankings (V2)
   * POST /api/v2/lineups/rankings
   * @param useOptimized - Whether to use the optimized vectorized approach
   */
  async getLeagueRankingsV2(
    nLineups: number = 1,
    nationality: string = 'India',
    useOptimized: boolean = true
  ): Promise<LeagueRankingsResponse> {
    return this.request(
      `/api/v2/lineups/rankings?use_optimized=${useOptimized}`,
      {
        method: 'POST',
        body: JSON.stringify({
          team_ids: [],  // Empty array means all teams
          n_lineups: nLineups,
          nationality: nationality
        }),
      }
    );
  }

  /**
   * Save league rankings to BigQuery (V2)
   * POST /api/v2/lineups/rankings/save
   */
  async saveLeagueRankings(
    rankings: TeamRanking[],
    season: number,
    overwrite: boolean = false
  ): Promise<SaveLeagueRankingsResponse> {
    return this.request(
      `/api/v2/lineups/rankings/save?overwrite=${overwrite}`,
      {
        method: 'POST',
        body: JSON.stringify({
          rankings,
          season,
          overwrite
        }),
      }
    );
  }

  /**
   * Check if saved league rankings exist in BigQuery (V2)
   * GET /api/v2/lineups/rankings/check
   */
  /** Saved league rankings (BigQuery trade_valuation.league_rankings): the latest version, or the version saved at `savedAt`. */
  async getSavedRankings(savedAt?: string): Promise<LeagueRankingsResponse & { saved_at?: string | null }> {
    const params = savedAt ? `?saved_at=${encodeURIComponent(savedAt)}` : '';
    return this.request(`/api/v2/lineups/rankings/saved${params}`);
  }

  /** Every saved version of the league rankings, newest first (saves append; nothing is overwritten). */
  async getRankingsHistory(): Promise<RankingsHistoryResponse> {
    return this.request(`/api/v2/lineups/rankings/history`);
  }

  async checkSavedRankings(): Promise<CheckSavedRankingsResponse> {
    return this.request(`/api/v2/lineups/rankings/check`, {
      method: 'GET',
    });
  }

  /**
   * Evaluate trade impact on team performance (V2)
   * POST /api/v2/lineups/evaluate-trade
   * @param useOptimized - Whether to use the optimized vectorized approach
   */
  async evaluateTradeV2(
    request: TradeEvaluationRequest,
    useOptimized: boolean = true
  ): Promise<TradeEvaluationResponse> {
    return this.request(`/api/v2/lineups/evaluate-trade?use_optimized=${useOptimized}`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  /**
   * Health check for V2 lineup service
   * GET /api/v2/lineups/health
   */
  async lineupHealthCheckV2(): Promise<{ status: string; service: string; version: string }> {
    return this.request('/api/v2/lineups/health');
  }

  /**
   * Reload data from BigQuery without restarting the app (V2)
   * POST /api/v2/lineups/reload-data
   * @param league - Optional league code to filter data (e.g., 'IPL', 'CPL', 'SA20')
   */
  async reloadDataV2(league?: string): Promise<{ success: boolean; message: string; players_count?: number; teams_count?: number }> {
    const params = league ? `?league=${encodeURIComponent(league)}` : '';
    return this.request(`/api/v2/lineups/reload-data${params}`, {
      method: 'POST',
    });
  }

  /**
   * Get team mappings with IDs, names, and colors (V2)
   * GET /api/v2/lineups/team-mappings
   * @param league - Optional league code to filter teams (e.g., 'IPL', 'CPL', 'SA20')
   */
  async getTeamMappingsV2(league?: string): Promise<any> {
    const params = league ? `?league=${encodeURIComponent(league)}` : '';
    return this.request(`/api/v2/lineups/team-mappings${params}`);
  }

  /**
   * Get team information with player details (V2)
   * POST /api/v2/lineups/team-info
   * @param league - Optional league code to filter data (e.g., 'IPL', 'CPL', 'SA20')
   */
  async getTeamInfoV2(request: TeamInfoRequest, league?: string): Promise<TeamInfoResponse> {
    console.log('Calling getTeamInfoV2 with request:', request, 'league:', league);
    const params = league ? `?league=${encodeURIComponent(league)}` : '';
    const response = await this.request<TeamInfoResponse>(`/api/v2/lineups/team-info${params}`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
    console.log('getTeamInfoV2 response:', response);
    return response;
  }

  // ==================== Lineup Playground APIs ====================

  /**
   * Evaluate a custom lineup with user-specified players and positions
   * POST /api/v2/lineups/playground/evaluate-custom
   */
  async evaluateCustomLineup(
    request: CustomLineupEvaluationRequest,
    useOptimized: boolean = true
  ): Promise<any> {
    // If request has player_names, convert to players with auto-assigned positions
    if (request.player_names && request.player_names.length > 0 && !request.players) {
      const players = request.player_names.map((playerName, index) => ({
        player_name: playerName,
        batting_position: index + 1,
      }));
      request = { ...request, players, player_names: undefined };
    }
    
    const response = await this.request<MatchLineupEvaluationResponse>(`/api/v2/lineups/playground/evaluate-custom`, {
      method: 'POST',
      body: JSON.stringify({
        ...request,
        is_bat_first: request.is_bat_first ?? 1,
        nationality: request.nationality ?? 'India',
      }),
    });
    
    // Convert MatchLineupEvaluationResponse to LineupEvaluation format
    return {
      lineup_num: response.lineup_num,
      lineup_players: response.lineup_players,
      sub_details: response.sub_details,
      bat_tv: response.bat_tv,
      bowl_tv: response.bowl_tv,
      '5_bowl_tv': response['5_bowl_tv'],
      '6_bowl_tv': response['6_bowl_tv'],
      '7_bowl_tv': response['7_bowl_tv'],
      '5_bowl_deployment': response['5_bowl_deployment'],
      '6_bowl_deployment': response['6_bowl_deployment'],
      '7_bowl_deployment': response['7_bowl_deployment'],
    };
  }

  /**
   * Evaluate lineups for a specific match from BigQuery
   * POST /api/v2/lineups/playground/evaluate-match
   */
  async evaluateMatchLineup(
    request: MatchLineupEvaluationRequest
  ): Promise<MatchLineupEvaluationResponse[]> {
    return this.request('/api/v2/lineups/playground/evaluate-match', {
      method: 'POST',
      body: JSON.stringify({
        ...request,
        start_season: request.start_season ?? 2023,
        comp_name: request.comp_name ?? 'IPL',
        nationality: request.nationality ?? 'India',
      }),
    });
  }

  /**
   * Get match lineup data from BigQuery
   * GET /api/v2/lineups/playground/match/{match_id}
   */
  async getMatchLineupData(
    matchId: number,
    startSeason: number = 2023,
    compName: string = 'IPL'
  ): Promise<MatchLineupDataResponse> {
    return this.request(
      `/api/v2/lineups/playground/match/${matchId}?start_season=${startSeason}&comp_name=${compName}`
    );
  }

  /**
   * Evaluate Custom Lineup for Playground (with full control over batting order and players)
   * POST /api/v2/lineups/playground/evaluate-custom-lineup
   */
  async evaluateCustomLineupPlayground(
    request: any
  ): Promise<MatchLineupEvaluationResponse> {
    return this.request(
      `/api/v2/lineups/playground/evaluate-custom-lineup`,
      {
        method: 'POST',
        body: JSON.stringify(request),
      }
    );
  }

  /**
   * Get all players from player pool (not just team-assigned players)
   * GET /api/v2/lineups/player-pool
   * @param league - Optional league code to filter players (e.g., 'IPL', 'CPL', 'SA20')
   */
  async getPlayerPool(league?: string): Promise<PlayerPoolResponse> {
    const params = league ? `?league=${encodeURIComponent(league)}` : '';
    return this.request(`/api/v2/lineups/player-pool${params}`);
  }

  // ==================== Salary Playground ====================

  /** Rostered players with contract prices, teams and retention rules for the Salary Playground. */
  async getSalaryPlayground(league: string = 'IPL', refresh: boolean = false): Promise<SalaryPlaygroundResponse> {
    const params = `?league=${encodeURIComponent(league)}${refresh ? '&refresh=true' : ''}`;
    return this.request(`/api/v2/salaries/playground${params}`);
  }

  // ==================== Batter Entry Tool APIs ====================

  /**
   * Optimize batting order using innings simulation + Hungarian algorithm
   * POST /api/v2/lineups/playground/optimize-batting-order
   */
  async optimizeBattingOrder(
    request: BattingOrderOptimizationRequest
  ): Promise<BattingOrderOptimizationResponse> {
    return this.request('/api/v2/lineups/playground/optimize-batting-order', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  /**
   * Recommend next batter given a game state
   * POST /api/v2/lineups/playground/recommend-next-batter
   */
  async recommendNextBatter(
    request: NextBatterRecommendationRequest
  ): Promise<NextBatterRecommendationResponse> {
    return this.request('/api/v2/lineups/playground/recommend-next-batter', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  // ==================== Projections APIs ====================

  /**
   * Get batter projections (V2)
   * GET /api/v2/projections/batters
   * @param targetSeason - Optional season. Defaults to the latest available season.
   */
  async getBatterProjections(targetSeason?: number): Promise<ProjectionsResponse> {
    const params = targetSeason != null ? `?target_season=${targetSeason}` : '';
    return this.request(`/api/v2/projections/batters${params}`);
  }

  /**
   * Get bowler projections (V2)
   * GET /api/v2/projections/bowlers
   * @param targetSeason - Optional season. Defaults to the latest available season.
   */
  async getBowlerProjections(targetSeason?: number): Promise<ProjectionsResponse> {
    const params = targetSeason != null ? `?target_season=${targetSeason}` : '';
    return this.request(`/api/v2/projections/bowlers${params}`);
  }

  /**
   * Rebuild the backend projections cache from BigQuery (manual refresh).
   * POST /api/v2/projections/refresh
   */
  async refreshProjections(): Promise<{ success: boolean; message: string }> {
    return this.request('/api/v2/projections/refresh', { method: 'POST' });
  }

  // ==================== Assistant APIs ====================

  /**
   * Ask the SquadLab Assistant a natural-language question.
   * POST /api/v2/agent/chat
   */
  async askAssistant(request: AssistantChatRequest): Promise<AssistantChatResponse> {
    return this.request('/api/v2/agent/chat', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  /**
   * SquadLab Auto-pilot — page-controlling agent (pilot: Lineups page)
   * POST /api/v2/agent/autopilot
   */
  async askAutopilot(request: AutopilotChatRequest): Promise<AutopilotChatResponse> {
    return this.request('/api/v2/agent/autopilot', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }
}

// Export singleton instance
export const apiService = new ApiService();
export default apiService;
